import { useCallback, useState } from "react";

import { langCodeToJapanese } from "@/lib/chillnn/lang-code";
import type { LangCode } from "@/lib/gql/__generated__/graphql";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { serverModelToInputModel } from "./converter/server-model-to-input-model";
import { useHotelSettingsHotelInfoI18nFormModelMethods } from "./methods";
import { useHotelI18nInfoUpdateMutation } from "../../repository/mutation/use-hotel-info-i18n-update-mutation";
import { viewModelToInput } from "../../repository/mutation/use-hotel-info-i18n-update-mutation/converter/view-model-to-input";
import { excludeKeys, type ExcludeKeys, type HotelSettingsHotelInfoI18nFormModel, type ServerModel } from "../../type";
import { hotelInfoI18nInputZodSchema } from "../../validation-schema";

import type { HotelInfoI18nZodSchemaType } from "../../validation-schema";

type Props = {
	serverModel: ServerModel;
	lang: LangCode;
};

type ViewModel = HotelSettingsHotelInfoI18nFormModel["ViewModel"];
type InputModel = HotelSettingsHotelInfoI18nFormModel["InputModel"];
type QueryData = HotelSettingsHotelInfoI18nFormModel["QueryData"];
export type HotelSettingsHotelInfoI18nFormModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export const useCreateHotelInfoI18nFormModel = ({ serverModel, lang }: Props) => {
	const inputModel = serverModelToInputModel({ serverModel });

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		HotelInfoI18nZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: hotelInfoI18nInputZodSchema,
	});

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: HotelSettingsHotelInfoI18nFormModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				HotelInfoI18nZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: hotelInfoI18nInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	const { onUpdate: _onUpdate } = useHotelI18nInfoUpdateMutation();
	/**
	 * @todo 副作用は UI で定義
	 */
	const onUpdate = useCallback(async () => {
		const { lang } = viewModel;
		const langCode = langCodeToJapanese(lang);
		const input = viewModelToInput({ viewModel });
		const { errors } = await _onUpdate({ input });

		if (!errors) {
			notifyUserEventInfo({
				title: `施設情報（${langCode}）を更新しました`,
			});
			onResetViewModel();
		}
	}, [_onUpdate, onResetViewModel, viewModel]);

	return {
		viewModel,
		onUpdate,
		...useHotelSettingsHotelInfoI18nFormModelMethods({ viewModel, onSetViewModel, lang }),
	};
};
